import { Routes } from '@angular/router';
import { AppShellComponent } from './shared/layout/app-shell/app-shell.component';
import { ComingSoonComponent } from './shared/coming-soon/coming-soon.component';
import { authGuard } from './core/helpers/auth.guard';
import { orderPreviewGuard } from './core/helpers/order-preview.guard';
import { abilityGuard, firstAllowedChildGuard, homeGuard } from './core/helpers/ability.guard';

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () => import('./features/login/login.component').then((m) => m.LoginComponent),
  },
  {
    // "Forgot password": request the reset email.
    path: 'forgot-password',
    loadComponent: () =>
      import('./features/forgot-password/forgot-password.component').then((m) => m.ForgotPasswordComponent),
  },
  {
    // Link sent by the reset email: create a new password.
    path: 'reset-password',
    loadComponent: () =>
      import('./features/reset-password/reset-password.component').then((m) => m.ResetPasswordComponent),
  },
  {
    // Link sent by email to invited users: complete the profile and create a password.
    path: 'accept-invite',
    loadComponent: () =>
      import('./features/accept-invite/accept-invite.component').then((m) => m.AcceptInviteComponent),
  },
  {
    path: 'settings',
    canActivate: [authGuard, abilityGuard('settings.manage')],
    loadComponent: () =>
      import('./features/settings/settings-shell.component').then((m) => m.SettingsShellComponent),
  },
  {
    path: 'account-settings',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/account-settings/account-settings.component').then(
        (m) => m.AccountSettingsComponent,
      ),
  },
  {
    path: '',
    component: AppShellComponent,
    canActivate: [authGuard],
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
      {
        // Shown by abilityGuard when the user's teams don't allow the page.
        path: 'no-access',
        loadComponent: () => import('./features/no-access/no-access.component').then((m) => m.NoAccessComponent),
      },
      {
        path: 'dashboard',
        canActivate: [homeGuard],
        loadComponent: () =>
          import('./features/dashboard/dashboard.component').then((m) => m.DashboardComponent),
      },
      {
        path: 'favorites',
        loadComponent: () =>
          import('./features/favorites/favorites.component').then((m) => m.FavoritesComponent),
      },
      {
        path: 'orders',
        children: [
          { path: '', pathMatch: 'full', canActivate: [firstAllowedChildGuard('/orders')], children: [] },
          {
            path: 'purchase-orders',
            canActivate: [abilityGuard('purchaseOrders.view')],
            loadComponent: () =>
              import('./features/purchase-orders/purchase-order-list/purchase-order-list.component').then(
                (m) => m.PurchaseOrderListComponent,
              ),
          },
          {
            path: 'purchase-orders/new',
            canActivate: [abilityGuard('purchaseOrders.create')],
            loadComponent: () =>
              import('./features/purchase-orders/purchase-order-create/purchase-order-create.component').then(
                (m) => m.PurchaseOrderCreateComponent,
              ),
          },
          {
            path: 'purchase-orders/:id',
            canActivate: [orderPreviewGuard('purchase-order'), abilityGuard('purchaseOrders.view')],
            loadComponent: () =>
              import('./features/purchase-orders/purchase-order-detail/purchase-order-detail.component').then(
                (m) => m.PurchaseOrderDetailComponent,
              ),
          },
          {
            path: 'sales-orders',
            canActivate: [abilityGuard('salesOrders.view')],
            loadComponent: () =>
              import('./features/sales-orders/sales-order-list/sales-order-list.component').then(
                (m) => m.SalesOrderListComponent,
              ),
          },
          {
            path: 'sales-orders/new',
            canActivate: [abilityGuard('salesOrders.create')],
            loadComponent: () =>
              import('./features/sales-orders/sales-order-create/sales-order-create.component').then(
                (m) => m.SalesOrderCreateComponent,
              ),
          },
          {
            path: 'sales-orders/:id',
            canActivate: [orderPreviewGuard('sales-order'), abilityGuard('salesOrders.view')],
            loadComponent: () =>
              import('./features/sales-orders/sales-order-detail/sales-order-detail.component').then(
                (m) => m.SalesOrderDetailComponent,
              ),
          },
          {
            path: 'flagged',
            canActivate: [abilityGuard(['purchaseOrders.view', 'salesOrders.view'])],
            component: ComingSoonComponent,
          },
        ],
      },
      {
        path: 'warehouse',
        children: [
          { path: '', pathMatch: 'full', canActivate: [firstAllowedChildGuard('/warehouse')], children: [] },
          {
            path: 'overview',
            canActivate: [abilityGuard(['inventory.view', 'lots.view'])],
            loadComponent: () =>
              import('./features/warehouse/warehouse-overview/warehouse-overview.component').then(
                (m) => m.WarehouseOverviewComponent,
              ),
          },
          {
            path: 'fulfillment',
            canActivate: [abilityGuard('fulfillment.view')],
            loadComponent: () =>
              import('./features/warehouse/warehouse-fulfillment/warehouse-fulfillment.component').then(
                (m) => m.WarehouseFulfillmentComponent,
              ),
          },
          {
            path: 'inventory',
            canActivate: [abilityGuard('inventory.view')],
            loadComponent: () =>
              import('./features/warehouse/inventory-list/inventory-list.component').then(
                (m) => m.InventoryListComponent,
              ),
          },
          {
            path: 'lots',
            canActivate: [abilityGuard('lots.view')],
            loadComponent: () =>
              import('./features/warehouse/lot-list/lot-list.component').then((m) => m.LotListComponent),
          },
          {
            path: 'repacks',
            canActivate: [abilityGuard('repacks.view')],
            loadComponent: () =>
              import('./features/warehouse/repack-list/repack-list.component').then((m) => m.RepackListComponent),
          },
          {
            path: 'supplies',
            canActivate: [abilityGuard(['inventory.view', 'purchaseOrders.view', 'salesOrders.view'])],
            loadComponent: () =>
              import('./features/warehouse/supplies/supplies.component').then((m) => m.SuppliesComponent),
          },
          {
            path: 'pallets',
            canActivate: [abilityGuard(['inventory.view', 'lots.view', 'purchaseOrders.view', 'salesOrders.view'])],
            loadComponent: () =>
              import('./features/warehouse/pallet-definitions/pallet-definitions.component').then(
                (m) => m.PalletDefinitionsComponent,
              ),
          },
          {
            path: 'definitions',
            canActivate: [abilityGuard(['inventory.view', 'lots.view'])],
            loadComponent: () =>
              import('./features/warehouse/warehouse-definitions/warehouse-definitions.component').then(
                (m) => m.WarehouseDefinitionsComponent,
              ),
          },
          {
            path: 'lots/:id',
            canActivate: [abilityGuard('lots.view')],
            loadComponent: () =>
              import('./features/warehouse/lot-detail/lot-detail.component').then(
                (m) => m.LotDetailComponent,
              ),
          },
        ],
      },
      {
        path: 'contacts',
        children: [
          { path: '', pathMatch: 'full', canActivate: [firstAllowedChildGuard('/contacts')], children: [] },
          {
            path: 'customers',
            canActivate: [abilityGuard('customers.view')],
            loadComponent: () =>
              import('./features/customers/customer-shell/customer-shell.component').then(
                (m) => m.CustomerShellComponent,
              ),
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./features/customers/customer-empty/customer-empty.component').then(
                    (m) => m.CustomerEmptyComponent,
                  ),
              },
              {
                path: 'new',
                canActivate: [abilityGuard('customers.manage')],
                loadComponent: () =>
                  import('./features/customers/customer-form/customer-form.component').then(
                    (m) => m.CustomerFormComponent,
                  ),
              },
              {
                path: ':id/edit',
                canActivate: [abilityGuard('customers.manage')],
                loadComponent: () =>
                  import('./features/customers/customer-form/customer-form.component').then(
                    (m) => m.CustomerFormComponent,
                  ),
              },
              {
                path: ':id',
                loadComponent: () =>
                  import('./features/customers/customer-detail/customer-detail.component').then(
                    (m) => m.CustomerDetailComponent,
                  ),
              },
            ],
          },
          {
            path: 'vendors',
            canActivate: [abilityGuard('vendors.view')],
            loadComponent: () =>
              import('./features/vendors/vendor-shell/vendor-shell.component').then(
                (m) => m.VendorShellComponent,
              ),
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./features/vendors/vendor-empty/vendor-empty.component').then(
                    (m) => m.VendorEmptyComponent,
                  ),
              },
              {
                path: 'new',
                canActivate: [abilityGuard('vendors.manage')],
                loadComponent: () =>
                  import('./features/vendors/vendor-form/vendor-form.component').then(
                    (m) => m.VendorFormComponent,
                  ),
              },
              {
                path: ':id/edit',
                canActivate: [abilityGuard('vendors.manage')],
                loadComponent: () =>
                  import('./features/vendors/vendor-form/vendor-form.component').then(
                    (m) => m.VendorFormComponent,
                  ),
              },
              {
                path: ':id',
                loadComponent: () =>
                  import('./features/vendors/vendor-detail/vendor-detail.component').then(
                    (m) => m.VendorDetailComponent,
                  ),
              },
            ],
          },
        ],
      },
      {
        path: 'finance',
        children: [
          { path: '', pathMatch: 'full', canActivate: [firstAllowedChildGuard('/finance')], children: [] },
          {
            path: 'overview',
            canActivate: [abilityGuard('finance.overview')],
            loadComponent: () =>
              import('./features/finance/finance-overview/finance-overview.component').then(
                (m) => m.FinanceOverviewComponent,
              ),
          },
          {
            path: 'expenses',
            canActivate: [abilityGuard('expenses.view')],
            loadComponent: () =>
              import('./features/finance/finance-expenses/finance-expenses.component').then(
                (m) => m.FinanceExpensesComponent,
              ),
          },
          {
            path: 'sales',
            canActivate: [abilityGuard(['payments.view', 'credits.view'])],
            loadComponent: () =>
              import('./features/finance/finance-sales/finance-sales.component').then(
                (m) => m.FinanceSalesComponent,
              ),
          },
          {
            path: 'ledgers',
            canActivate: [abilityGuard('ledgers.view')],
            loadComponent: () =>
              import('./features/finance/finance-ledgers/finance-ledgers.component').then(
                (m) => m.FinanceLedgersComponent,
              ),
          },
          {
            path: 'chart-of-accounts',
            canActivate: [abilityGuard('chartOfAccounts.view')],
            loadComponent: () =>
              import('./features/finance/finance-chart-of-accounts/finance-chart-of-accounts.component').then(
                (m) => m.FinanceChartOfAccountsComponent,
              ),
          },
          {
            path: 'internal-costs',
            canActivate: [abilityGuard('expenses.view')],
            loadComponent: () =>
              import('./features/finance/finance-internal-costs/finance-internal-costs.component').then(
                (m) => m.FinanceInternalCostsComponent,
              ),
          },
        ],
      },
      {
        path: 'reports',
        children: [
          { path: '', pathMatch: 'full', canActivate: [firstAllowedChildGuard('/reports')], children: [] },
          {
            path: 'sales',
            canActivate: [abilityGuard('reports.sales')],
            loadComponent: () =>
              import('./features/reports/reports-sales/reports-sales.component').then(
                (m) => m.ReportsSalesComponent,
              ),
          },
          {
            path: 'inventory',
            canActivate: [abilityGuard('reports.inventory')],
            loadComponent: () =>
              import('./features/reports/reports-inventory/reports-inventory.component').then(
                (m) => m.ReportsInventoryComponent,
              ),
          },
          {
            path: 'financial',
            canActivate: [abilityGuard('reports.financial')],
            loadComponent: () =>
              import('./features/reports/reports-financial/reports-financial.component').then(
                (m) => m.ReportsFinancialComponent,
              ),
          },
          {
            path: 'audit-log',
            canActivate: [abilityGuard('reports.auditLog')],
            loadComponent: () =>
              import('./features/reports/reports-audit-log/reports-audit-log.component').then(
                (m) => m.ReportsAuditLogComponent,
              ),
          },
        ],
      },
    ],
  },
  { path: '**', redirectTo: 'dashboard' },
];
